import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, Flex, Box, Text, Input, Kbd,
} from '@chakra-ui/react';
import { LuSearch, LuArrowRight } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../contexts/FilterContext';

/**
 * ⌘K: one field to jump to any page, or to search the movements for what was
 * typed. Searching opens Movements with the list's own text filter, so the
 * results are the Movements list, not a second list of its own.
 *
 * `pages` are the sidebar's items: `{ label, to, icon }`.
 */
export default function CommandMenu({ isOpen, onClose, pages }) {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { addFilter } = useFilters();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (isOpen) { setQuery(''); setActive(0); }
  }, [isOpen]);

  const text = query.trim();
  const items = useMemo(() => {
    const matching = pages.filter((p) => !text || p.label.toLowerCase().includes(text.toLowerCase()));
    const jumps = matching.map((p) => ({ key: p.to, label: p.label, icon: p.icon, run: () => navigate(p.to) }));
    if (!text) return jumps;
    const search = {
      key: 'search',
      label: t('commandMenu.searchMovements', { q: text }),
      icon: <LuSearch />,
      run: () => {
        addFilter({ entity: 'Movement', column: 'note', operator: 'ilike', values: [text] });
        navigate('/movements');
      },
    };
    return [search, ...jumps];
  }, [pages, text, t, navigate, addFilter]);

  const run = (item) => {
    if (!item) return;
    onClose();
    item.run();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      run(items[active]);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" motionPreset="slideInTop">
      <ModalOverlay />
      <ModalContent mt="14vh" overflow="hidden">
        <Flex align="center" gap={3} px={4} h="52px" borderBottom="1px solid" borderColor="redesign.border">
          <Box color="redesign.text.muted" display="inline-flex" aria-hidden="true"><LuSearch size={16} /></Box>
          <Input
            variant="unstyled"
            fontSize="md"
            placeholder={t('commandMenu.placeholder')}
            aria-label={t('commandMenu.placeholder')}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            autoFocus
          />
          <Kbd fontSize="2xs" color="redesign.text.muted" bg="redesign.bg.sunken" borderColor="redesign.border">esc</Kbd>
        </Flex>
        <Box role="listbox" aria-label={t('commandMenu.jumpTo')} py={2} maxH="360px" overflowY="auto">
          {!text && <Text textStyle="microLabel" px={4} pt={1} pb={2}>{t('commandMenu.jumpTo')}</Text>}
          {items.map((item, i) => (
            <Flex
              key={item.key}
              role="option"
              aria-selected={i === active}
              align="center"
              gap={3}
              h="40px"
              px={4}
              cursor="pointer"
              bg={i === active ? 'redesign.bg.hover' : 'transparent'}
              color={i === active ? 'redesign.heading' : 'redesign.text'}
              onMouseEnter={() => setActive(i)}
              onClick={() => run(item)}
            >
              <Box color="redesign.text.muted" display="inline-flex" fontSize="16px" aria-hidden="true">{item.icon}</Box>
              <Text flex={1} fontSize="md" fontWeight={500} isTruncated>{item.label}</Text>
              {i === active && (
                <Box color="redesign.text.muted" display="inline-flex" aria-hidden="true"><LuArrowRight size={14} /></Box>
              )}
            </Flex>
          ))}
          {items.length === 0 && (
            <Text px={4} py={3} fontSize="md" color="redesign.text.muted">{t('commandMenu.noResults')}</Text>
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
}
